import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faBellOn } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Popover from "./Popover";
import { useEffect, useState } from "react";
import alertHelper from "../../helpers/alertHelper";
import FormattedDate from "./FormattedDate";
import UserAlertDetails from "../../types/dtos/alert/UserAlertDetails";
import { useAuth } from "react-oidc-context";
import alertApi from "../../api/alert/alertApi";
import ModalPopup from "./ModalPopup";
import RecentAlert from "../../types/dtos/alert/RecentAlert";
//@ts-ignore
import Pagination from "react-js-pagination";

export interface AlertMenuProps {
  defaultAlertData: UserAlertDetails;
}

const AlertMenu = ({ defaultAlertData }: AlertMenuProps) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const api = new alertApi(auth.user?.access_token);

  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalAlerts, setModalAlerts] = useState<RecentAlert[]>();
  const [modalDisplayAlerts, setModalDisplayAlerts] = useState<RecentAlert[]>();
  const [modalViewedAlertIds, setModalViewedAlertIds] = useState<number[]>([]);
  const [activePageNumber, setActivePageNumber] = useState<number>(1);
  const [itemsPerPage] = useState<number>(5);
  const [totalItemCount, setTotalItemCount] = useState<number>();

  const [alertData, setAlertData] =
    useState<UserAlertDetails>(defaultAlertData);
  const [delayedUpdatedData, setDelayedUpdatedData] =
    useState<UserAlertDetails>();
  const recentUnreadAlertsCount = alertData.recentNotifications.filter(
    (x) => x.dateRead == null,
  ).length;

  useEffect(() => {
    if (defaultAlertData.totalNotificationCount > 0) {
      alertHelper.processAlertDetails(defaultAlertData);
      setAlertData(defaultAlertData);
    }
  }, [defaultAlertData]);

  // |----- POPOVER EVENTS -----|
  const onOpenChangeHandler = (value: boolean) => {
    setPopoverIsOpen(value);

    if (alertData != undefined) {
      if (value) {
        const anyRecentAlertsAreUnread =
          alertData.recentNotifications.filter((x) => x.dateRead == null)
            .length > 0;

        if (anyRecentAlertsAreUnread) {
          const recentAlertIds = alertData.recentNotifications.map((x) => x.id);

          api.markGivenAlertsAsRead(
            true,
            recentAlertIds,
            (json: UserAlertDetails) => {
              alertHelper.processAlertDetails(json);
              setDelayedUpdatedData(json);
            },
            (error: any) => {
              console.error(error);
            },
          );
        }
      } else {
        if (delayedUpdatedData != undefined) {
          setAlertData(delayedUpdatedData);
          setDelayedUpdatedData(undefined);
        }
      }
    }
  };

  const onPopoverMarkAllAsReadClickHandler = () => {
    api.markAllAlertsAsRead(
      true,
      (json: UserAlertDetails) => {
        alertHelper.processAlertDetails(json);
        setAlertData(json);

        if (delayedUpdatedData != undefined) {
          setDelayedUpdatedData(undefined);
        }
      },
      (error: any) => {
        console.error(error);
      },
    );
  };

  const onViewMoreClickHandler = () => {
    api.getAlertPanelDataForEmployee(
      (json: UserAlertDetails) => {
        alertHelper.processAlertDetails(json);
        setModalAlerts(json.recentNotifications);

        let displayItems = [...json.recentNotifications].splice(
          0,
          itemsPerPage,
        );
        setModalDisplayAlerts(displayItems);

        updateViewedAlertIds(displayItems);

        setActivePageNumber(1);
        setTotalItemCount(json.totalNotificationCount);
        setModalIsOpen(true);
      },
      (error: any) => {
        console.error(error);
      },
    );
  };

  // |----- MODAL EVENTS -----|
  const onModalCloseChangeHandler = (value: boolean) => {
    setModalIsOpen(value);

    api.markGivenAlertsAsRead(
      false,
      modalViewedAlertIds,
      (json: UserAlertDetails) => {
        alertHelper.processAlertDetails(json);

        const newPopoverState: UserAlertDetails = {
          totalNotificationCount: json.totalNotificationCount,
          totalUnreadNotificationCount: json.totalUnreadNotificationCount,
          recentNotifications: json.recentNotifications.slice(0, 3),
          notificationTypes: json.notificationTypes,
        };
        setAlertData(newPopoverState);
        setModalViewedAlertIds([]);
      },
      (error: any) => {
        console.error(error);
      },
    );
  };

  const onModalMarkAllAsReadClickHandler = () => {
    api.markAllAlertsAsRead(
      false,
      (json: UserAlertDetails) => {
        alertHelper.processAlertDetails(json);
        setModalAlerts(json.recentNotifications);
        setModalDisplayAlerts(
          [...json.recentNotifications].splice(0, itemsPerPage),
        );
        setActivePageNumber(1);
        setTotalItemCount(json.totalNotificationCount);

        const newPopoverState: UserAlertDetails = {
          totalNotificationCount: json.totalNotificationCount,
          totalUnreadNotificationCount: json.totalUnreadNotificationCount,
          recentNotifications: json.recentNotifications.slice(0, 3),
          notificationTypes: json.notificationTypes,
        };
        setAlertData(newPopoverState);
      },
      (error: any) => {
        console.error(error);
      },
    );
  };

  const handlePaginationChange = (pageNumber: number) => {
    if (modalAlerts != undefined) {
      var startIndex = (pageNumber - 1) * itemsPerPage;
      let displayItems = [...modalAlerts].splice(startIndex, itemsPerPage);

      updateViewedAlertIds(displayItems);

      setModalDisplayAlerts(displayItems);
      setActivePageNumber(pageNumber);
    }
  };

  const updateViewedAlertIds = (alerts: RecentAlert[]) => {
    let unreadAlertsOnly = alerts.filter((x) => x.dateRead == null);
    let unreadAlertIds = unreadAlertsOnly.map((x) => x.id);
    setModalViewedAlertIds([...modalViewedAlertIds, ...unreadAlertIds]);
  };

  return (
    <div className="print:hidden">
      <Popover
        openState={popoverIsOpen}
        onOpenStateChange={onOpenChangeHandler}
        placement="bottom"
        showCloseIcon={false}
        contentClassNames="w-72 bg-white"
        triggerElement={
          <button className="inline-flex justify-end lg:justify-center w-full bg-white text-sm font-medium text-gray-700 hover:lg:bg-gray-100 rounded-full focus:outline-none">
            {alertData.totalUnreadNotificationCount == 0 && (
              <FontAwesomeIcon
                icon={faBell}
                size="lg"
                className="text-gray-500 m-4"
              />
            )}
            {alertData.totalUnreadNotificationCount > 0 && (
              <FontAwesomeIcon
                icon={faBellOn}
                size="lg"
                className="text-yellow-400 m-4"
              />
            )}
          </button>
        }
      >
        <>
          <div className="flex flex-row justify-between py-1 px-4 border-b border-b-gray-200 bg-gray-100 rounded-t-lg">
            <span>{t("Notifications.Heading")}</span>

            {alertData.totalUnreadNotificationCount > 0 && (
              <button
                onClick={onPopoverMarkAllAsReadClickHandler}
                className="justify-end text-xs font-normal underline self-center"
              >
                {t("Notifications.MarkAllAsRead")}
              </button>
            )}
          </div>
          {alertData.totalNotificationCount == 0 && (
            <div className="p-2 px-4">
              <p className="text-sm text-gray-600">
                {t("Notifications.NoNotifications")}
              </p>
            </div>
          )}
          {alertData.totalNotificationCount > 0 && (
            <>
              <div className="divide-y">
                {alertData.recentNotifications.map((alert) => (
                  <div key={alert.id}>
                    {/* Clickable Alert */}
                    {alert.href != null && (
                      <Link
                        to={decodeURIComponent(alert.href)}
                        onClick={() => setPopoverIsOpen(false)}
                        className="block my-1 hover:bg-gray-100 py-2 px-4"
                      >
                        <div className="flex flex-row">
                          {alert.dateRead == null && (
                            <div className="pr-2 pt-1">
                              <span className="flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-yellow-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400"></span>
                              </span>
                            </div>
                          )}
                          {alert.dateRead != null && (
                            <div className="pr-4">&nbsp;</div>
                          )}
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-600">
                              {alert.message}
                            </span>
                            <span className="text-xs text-gray-400 underline">
                              <FormattedDate
                                date={alert.dateSent}
                                displayMode="DATE-ONLY"
                              />
                            </span>
                          </div>
                        </div>
                      </Link>
                    )}

                    {/* Non-clickable Alert */}
                    {alert.href == null && (
                      <div className="block py-2 px-4">
                        <div className="flex flex-row">
                          {alert.dateRead == null && (
                            <div className="pr-2 pt-1">
                              <span className="flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-yellow-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400"></span>
                              </span>
                            </div>
                          )}
                          {alert.dateRead != null && (
                            <div className="pr-4">&nbsp;</div>
                          )}
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-600">
                              {alert.message}
                            </span>
                            <span className="text-xs text-gray-400 underline">
                              <FormattedDate
                                date={alert.dateSent}
                                displayMode="DATE-ONLY"
                              />
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {alertData.totalNotificationCount > 3 && (
                <div className="!mt-0 border-t border-b-gray-200 bg-gray-100 rounded-b-lg py-0.5 text-center hover:bg-gray-300">
                  <div className="w-full">
                    <button
                      onClick={onViewMoreClickHandler}
                      className="w-full text-sm font-normal"
                    >
                      {t("Notifications.ViewMore")}{" "}
                      {alertData.totalUnreadNotificationCount -
                        recentUnreadAlertsCount !=
                        0 && (
                        <span>
                          (
                          {alertData.totalUnreadNotificationCount -
                            recentUnreadAlertsCount}
                          )
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      </Popover>

      <ModalPopup
        isOpen={modalIsOpen}
        onOpenChange={onModalCloseChangeHandler}
        onPrimaryButtonClick={() => onModalCloseChangeHandler(false)}
        primaryButtonText={t("Close")}
        title={t("Notifications.Heading")}
        showCloseIcon={false}
      >
        {alertData.totalUnreadNotificationCount > 0 && (
          <div className="block text-right">
            <button
              onClick={onModalMarkAllAsReadClickHandler}
              className="text-xs font-normal underline self-center"
            >
              {t("Notifications.MarkAllAsRead")}
            </button>
          </div>
        )}
        <div className="divide-y">
          {modalDisplayAlerts &&
            modalDisplayAlerts.map((alert) => (
              <div key={alert.id}>
                {/* Clickable Alert */}
                {alert.href != null && (
                  <Link
                    to={decodeURIComponent(alert.href)}
                    onClick={() => setModalIsOpen(false)}
                    className="block my-1 hover:bg-gray-100 py-2 px-4"
                  >
                    <div className="flex flex-row">
                      {alert.dateRead == null && (
                        <div className="pr-2 pt-1">
                          <span className="flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-yellow-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400"></span>
                          </span>
                        </div>
                      )}
                      {alert.dateRead != null && (
                        <div className="pr-4">&nbsp;</div>
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-600">
                          {alert.message}
                        </span>
                        <span className="text-xs text-gray-400 underline">
                          <FormattedDate
                            date={alert.dateSent}
                            displayMode="DATE-ONLY"
                          />
                        </span>
                      </div>
                    </div>
                  </Link>
                )}

                {/* Non-clickable Alert */}
                {alert.href == null && (
                  <div className="block py-2 px-4">
                    <div className="flex flex-row">
                      {alert.dateRead == null && (
                        <div className="pr-2 pt-1">
                          <span className="flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-yellow-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400"></span>
                          </span>
                        </div>
                      )}
                      {alert.dateRead != null && (
                        <div className="pr-4">&nbsp;</div>
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-600">
                          {alert.message}
                        </span>
                        <span className="text-xs text-gray-400 underline">
                          <FormattedDate
                            date={alert.dateSent}
                            displayMode="DATE-ONLY"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>

        {modalDisplayAlerts && modalDisplayAlerts.length > 0 && (
          <div className="ml-9">
            <Pagination
              activePage={activePageNumber}
              itemsCountPerPage={itemsPerPage}
              totalItemsCount={totalItemCount}
              pageRangeDisplayed={5}
              onChange={handlePaginationChange}
              activeClass="pagination-active-list-item"
              itemClass="pagination-list-item"
              itemClassFirst="pagination-first-item"
              itemClassLast="pagination-last-item"
            />
          </div>
        )}
      </ModalPopup>
    </div>
  );
};

export default AlertMenu;
